import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  DollarSign, 
  Users, 
  FileText, 
  Download, 
  Plus, 
  Edit, 
  Trash2, 
  Check,
  X,
  Building2,
  CreditCard,
  Calendar,
  RefreshCw,
  IndianRupee,
  Wallet,
  TrendingUp,
  Calculator,
  Eye,
  ChevronLeft,
  ChevronRight,
  Search,
  UserCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Reusable editor for per-employee additional earnings (Bonus, Double Pay, etc.)
const AdditionalEarningsEditor = ({ items, onChange }) => {
  const addItem = () => onChange([...items, { label: '', amount: '' }]);
  const updateItem = (i, field, val) => onChange(items.map((it, idx) => (idx === i ? { ...it, [field]: val } : it)));
  const removeItem = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div>
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Additional Earnings (Bonus, Double Pay, etc.)</Label>
        <Button type="button" size="sm" variant="outline" className="gap-1 h-8" onClick={addItem} data-testid="add-earning-btn">
          <Plus className="h-3 w-3" /> Add
        </Button>
      </div>
      {items.length === 0 && (
        <p className="text-xs text-gray-400 mt-1">No additional earnings. Click "Add" to include a bonus or extra pay.</p>
      )}
      <div className="space-y-2 mt-2">
        {items.map((it, i) => (
          <div key={i} className="flex gap-2 items-center">
            <Input
              placeholder="Label (e.g. Diwali Bonus)"
              value={it.label}
              onChange={(e) => updateItem(i, 'label', e.target.value)}
              data-testid={`earning-label-${i}`}
            />
            <div className="relative w-40 flex-shrink-0">
              <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="number"
                placeholder="0"
                className="pl-9"
                value={it.amount}
                onChange={(e) => updateItem(i, 'amount', e.target.value)}
                data-testid={`earning-amount-${i}`}
              />
            </div>
            <Button type="button" size="icon" variant="ghost" className="text-red-500 flex-shrink-0" onClick={() => removeItem(i)} data-testid={`remove-earning-${i}`}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

const PayrollPage = () => {
  const { user, isAdmin } = useAuth();
  const token = localStorage.getItem('omnilink_token');
  
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [payrolls, setPayrolls] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // Modals
  const [showSalaryModal, setShowSalaryModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  
  // Form states
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [salaryForm, setSalaryForm] = useState({
    // Employee details for payslip
    employee_name_on_slip: '',
    employee_code: '',
    designation: '',
    department: '',
    date_of_joining: '',
    location: '',
    // Salary
    monthly_ctc: '',
    // Bank details
    bank_name: '',
    bank_account_no: '',
    ifsc_code: '',
    pan_number: '',
    uan_number: ''
  });
  const [generateForm, setGenerateForm] = useState({
    user_id: '',
    paid_days: '',
    total_days: '',
    incentive: '0',
    loan_deduction: '0',
    additional_earnings: []
  });
  const [viewPayroll, setViewPayroll] = useState(null);
  
  // Edit Payroll state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editPayroll, setEditPayroll] = useState(null);
  const [editForm, setEditForm] = useState({
    paid_days: '',
    incentive: '',
    loan_deduction: '',
    additional_earnings: []
  });
  
  // Search
  const [searchTerm, setSearchTerm] = useState('');

  const fetchWithAuth = useCallback(async (endpoint) => {
    const response = await fetch(`${API_URL}${endpoint}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Request failed');
    return response.json();
  }, [token]);

  const postWithAuth = useCallback(async (endpoint, data) => {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    return response.json();
  }, [token]);

  const putWithAuth = useCallback(async (endpoint, data) => {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'PUT',
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    return response.json();
  }, [token]);

  const fetchEmployees = useCallback(async () => {
    try {
      const data = await fetchWithAuth('/api/payroll/all-salaries');
      setEmployees(data.employees || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  }, [fetchWithAuth]);

  const fetchPayrolls = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchWithAuth(`/api/payroll/list?month=${selectedMonth}&year=${selectedYear}`);
      setPayrolls(data.payrolls || []);
    } catch (error) {
      console.error('Error fetching payrolls:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, selectedMonth, selectedYear]);

  useEffect(() => {
    if (isAdmin()) {
      fetchEmployees();
      fetchPayrolls();
    }
  }, [fetchEmployees, fetchPayrolls, isAdmin]);

  useEffect(() => {
    if (isAdmin()) {
      fetchPayrolls();
    }
  }, [selectedMonth, selectedYear, fetchPayrolls, isAdmin]);

  const handleSaveSalary = async () => {
    if (!selectedEmployee || !salaryForm.monthly_ctc) {
      toast.error('Please enter monthly CTC');
      return;
    }
    
    try {
      setLoading(true);
      const result = await postWithAuth('/api/payroll/salary-structure', {
        user_id: selectedEmployee.user_id,
        monthly_ctc: parseFloat(salaryForm.monthly_ctc),
        // Employee details
        employee_name_on_slip: salaryForm.employee_name_on_slip || null,
        employee_code: salaryForm.employee_code || null,
        designation: salaryForm.designation || null,
        department: salaryForm.department || null,
        date_of_joining: salaryForm.date_of_joining || null,
        location: salaryForm.location || null,
        // Bank details
        bank_name: salaryForm.bank_name || null,
        bank_account_no: salaryForm.bank_account_no || null,
        ifsc_code: salaryForm.ifsc_code || null,
        pan_number: salaryForm.pan_number || null,
        uan_number: salaryForm.uan_number || null
      });
      
      if (result.message) {
        toast.success('Employee payroll setup saved successfully');
        setShowSalaryModal(false);
        fetchEmployees();
      } else {
        toast.error(result.detail || 'Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save payroll setup');
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePayroll = async () => {
    if (!generateForm.user_id || !generateForm.paid_days || !generateForm.total_days) {
      toast.error('Please fill all required fields');
      return;
    }
    
    try {
      setLoading(true);
      const result = await postWithAuth('/api/payroll/generate', {
        user_id: generateForm.user_id,
        month: selectedMonth,
        year: selectedYear,
        paid_days: parseFloat(generateForm.paid_days),
        total_days: parseFloat(generateForm.total_days),
        incentive: parseFloat(generateForm.incentive) || 0,
        loan_deduction: parseFloat(generateForm.loan_deduction) || 0,
        additional_earnings: generateForm.additional_earnings
          .filter((e) => e.label && e.label.trim() && parseFloat(e.amount))
          .map((e) => ({ label: e.label.trim(), amount: parseFloat(e.amount) }))
      });
      
      if (result.id) {
        toast.success(`Payroll generated for ${result.employee_name}`);
        setShowGenerateModal(false);
        setGenerateForm({ user_id: '', paid_days: '', total_days: '', incentive: '0', loan_deduction: '0', additional_earnings: [] });
        fetchPayrolls();
      } else {
        toast.error(result.detail || 'Failed to generate payroll');
      }
    } catch (error) {
      toast.error('Failed to generate payroll');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadSlip = async (payrollId, employeeName, month, year) => {
    try {
      const response = await fetch(`${API_URL}/api/payroll/${payrollId}/slip`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const monthNames = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      a.download = `Payslip_${employeeName.replace(/\s+/g, '_')}_${monthNames[month]}_${year}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Payslip downloaded');
    } catch (error) {
      toast.error('Failed to download payslip');
    }
  };

  const handleDeletePayroll = async (payroll) => {
    if (!window.confirm(`Delete the payroll for ${payroll.employee_name} (${monthNames[payroll.month - 1]} ${payroll.year})? This cannot be undone. You can then regenerate it with corrected values.`)) return;
    try {
      const response = await fetch(`${API_URL}/api/payroll/${payroll.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(data.detail || 'Failed to delete payroll');
        return;
      }
      toast.success('Payroll deleted. You can now regenerate it.');
      fetchPayrolls();
    } catch (error) {
      toast.error('Failed to delete payroll');
    }
  };

  // Open Edit Payroll Modal
  const openEditPayroll = (payroll) => {
    setEditPayroll(payroll);
    setEditForm({
      paid_days: payroll.paid_days.toString(),
      incentive: payroll.incentive.toString(),
      loan_deduction: payroll.loan_deduction.toString(),
      additional_earnings: (payroll.additional_earnings || []).map((e) => ({ label: e.label, amount: e.amount.toString() }))
    });
    setShowEditModal(true);
  };

  // Handle Update Payroll
  const handleUpdatePayroll = async () => {
    if (!editPayroll) return;
    
    if (!editForm.paid_days || parseFloat(editForm.paid_days) < 0) {
      toast.error('Please enter valid paid days');
      return;
    }
    
    if (parseFloat(editForm.paid_days) > editPayroll.total_days) {
      toast.error(`Paid days cannot exceed total days (${editPayroll.total_days})`);
      return;
    }
    
    try {
      setLoading(true);
      const result = await putWithAuth(`/api/payroll/${editPayroll.id}`, {
        paid_days: parseFloat(editForm.paid_days),
        incentive: parseFloat(editForm.incentive) || 0,
        loan_deduction: parseFloat(editForm.loan_deduction) || 0,
        additional_earnings: editForm.additional_earnings
          .filter((e) => e.label && e.label.trim() && parseFloat(e.amount))
          .map((e) => ({ label: e.label.trim(), amount: parseFloat(e.amount) }))
      });
      
      if (result.message) {
        toast.success('Payroll updated successfully! New payslip will reflect the changes.');
        setShowEditModal(false);
        setEditPayroll(null);
        fetchPayrolls();
      } else {
        toast.error(result.detail || 'Failed to update payroll');
      }
    } catch (error) {
      toast.error('Failed to update payroll');
    } finally {
      setLoading(false);
    }
  };

  const openEditSalary = (employee) => {
    setSelectedEmployee(employee);
    setSalaryForm({
      // Employee details
      employee_name_on_slip: employee.employee_name_on_slip || employee.employee_name || '',
      employee_code: employee.employee_code || '',
      designation: employee.designation || '',
      department: employee.department || '',
      date_of_joining: employee.date_of_joining ? employee.date_of_joining.split('T')[0] : '',
      location: employee.location || '',
      // Salary
      monthly_ctc: employee.monthly_ctc || '',
      // Bank details
      bank_name: employee.bank_name || '',
      bank_account_no: employee.bank_account_no || '',
      ifsc_code: employee.ifsc_code || '',
      pan_number: employee.pan_number || '',
      uan_number: employee.uan_number || ''
    });
    setShowSalaryModal(true);
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('en-IN', { 
      style: 'currency', 
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];

  const filteredEmployees = employees.filter(e => 
    e.employee_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate summary stats
  const totalPayroll = payrolls.reduce((sum, p) => sum + (p.net_payable || 0), 0);
  const totalIncentives = payrolls.reduce((sum, p) => sum + (p.incentive || 0), 0);
  const employeesWithCTC = employees.filter(e => e.has_salary_structure).length;

  if (!isAdmin()) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Access denied. Admin only.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payroll-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payroll Management</h1>
          <p className="text-gray-500 mt-1">Manage employee salaries and generate payslips</p>
        </div>
        <div className="flex gap-2">
          <Select value={`${selectedMonth}`} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthNames.map((name, idx) => (
                <SelectItem key={idx} value={`${idx + 1}`}>{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={`${selectedYear}`} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026, 2027].map(y => (
                <SelectItem key={y} value={`${y}`}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Employees</p>
                <p className="text-2xl font-bold text-blue-600">{employees.length}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">{employeesWithCTC} with salary set</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Payrolls Generated</p>
                <p className="text-2xl font-bold text-green-600">{payrolls.length}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <FileText className="h-5 w-5 text-green-600" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">{monthNames[selectedMonth - 1]} {selectedYear}</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Payroll</p>
                <p className="text-2xl font-bold text-purple-600">{formatCurrency(totalPayroll)}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <Wallet className="h-5 w-5 text-purple-600" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">This month</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Incentives</p>
                <p className="text-2xl font-bold text-orange-600">{formatCurrency(totalIncentives)}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <TrendingUp className="h-5 w-5 text-orange-600" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">Paid this month</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="salaries" className="space-y-4">
        <TabsList className="grid w-full grid-cols-2 lg:w-auto lg:inline-grid">
          <TabsTrigger value="salaries" className="gap-2">
            <Users className="h-4 w-4" />
            Salary Structures
          </TabsTrigger>
          <TabsTrigger value="payrolls" className="gap-2">
            <FileText className="h-4 w-4" />
            Generated Payrolls
          </TabsTrigger>
        </TabsList>

        {/* Salary Structures Tab */}
        <TabsContent value="salaries">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Employee Salary Structures</CardTitle>
                  <CardDescription>Set CTC and bank details for each employee</CardDescription>
                </div>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search employees..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-3 font-medium">Employee</th>
                      <th className="pb-3 font-medium">Role</th>
                      <th className="pb-3 font-medium">Monthly CTC</th>
                      <th className="pb-3 font-medium">Bank Details</th>
                      <th className="pb-3 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmployees.map(emp => (
                      <tr key={emp.user_id} className="border-b hover:bg-gray-50">
                        <td className="py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                              <UserCircle className="h-5 w-5 text-indigo-600" />
                            </div>
                            <div>
                              <p className="font-medium">{emp.employee_name}</p>
                              <p className="text-xs text-gray-500">{emp.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3">
                          <Badge variant="outline" className="capitalize">{emp.role}</Badge>
                        </td>
                        <td className="py-3">
                          {emp.monthly_ctc ? (
                            <span className="font-semibold text-green-600">{formatCurrency(emp.monthly_ctc)}</span>
                          ) : (
                            <span className="text-gray-400">Not set</span>
                          )}
                        </td>
                        <td className="py-3">
                          {emp.bank_account_no ? (
                            <div className="text-sm">
                              <p>{emp.bank_name || 'Bank'}</p>
                              <p className="text-xs text-gray-500">****{emp.bank_account_no.slice(-4)}</p>
                            </div>
                          ) : (
                            <span className="text-gray-400">Not set</span>
                          )}
                        </td>
                        <td className="py-3">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditSalary(emp)}
                            className="gap-1"
                          >
                            <Edit className="h-3 w-3" />
                            {emp.has_salary_structure ? 'Edit' : 'Set'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Generated Payrolls Tab */}
        <TabsContent value="payrolls">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Generated Payrolls - {monthNames[selectedMonth - 1]} {selectedYear}</CardTitle>
                  <CardDescription>View and download employee payslips</CardDescription>
                </div>
                <Button onClick={() => setShowGenerateModal(true)} className="gap-2">
                  <Plus className="h-4 w-4" />
                  Generate Payroll
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
                </div>
              ) : payrolls.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="font-medium">No payrolls generated</p>
                  <p className="text-sm">Generate payroll for employees for {monthNames[selectedMonth - 1]} {selectedYear}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="pb-3 font-medium">Employee</th>
                        <th className="pb-3 font-medium">Paid Days</th>
                        <th className="pb-3 font-medium">Gross</th>
                        <th className="pb-3 font-medium">Incentive</th>
                        <th className="pb-3 font-medium">Deductions</th>
                        <th className="pb-3 font-medium">Net Payable</th>
                        <th className="pb-3 font-medium">Status</th>
                        <th className="pb-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payrolls.map(payroll => (
                        <tr key={payroll.id} className="border-b hover:bg-gray-50">
                          <td className="py-3">
                            <div>
                              <p className="font-medium">{payroll.employee_name}</p>
                              <p className="text-xs text-gray-500">{payroll.employee_code}</p>
                            </div>
                          </td>
                          <td className="py-3">
                            <span className="text-sm">{payroll.paid_days} / {payroll.total_days}</span>
                            {payroll.lop_days > 0 && (
                              <span className="text-xs text-red-500 ml-1">({payroll.lop_days} LOP)</span>
                            )}
                          </td>
                          <td className="py-3 font-medium">{formatCurrency(payroll.gross_earnings)}</td>
                          <td className="py-3">
                            {payroll.incentive > 0 ? (
                              <span className="text-green-600">{formatCurrency(payroll.incentive)}</span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="py-3 text-red-600">{formatCurrency(payroll.total_deductions)}</td>
                          <td className="py-3 font-bold text-lg">{formatCurrency(payroll.net_payable)}</td>
                          <td className="py-3">
                            <Badge 
                              variant="outline"
                              className={payroll.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}
                            >
                              {payroll.status}
                            </Badge>
                          </td>
                          <td className="py-3">
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => { setViewPayroll(payroll); setShowViewModal(true); }}
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {payroll.status !== 'paid' && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => openEditPayroll(payroll)}
                                  title="Edit Payroll"
                                  className="text-orange-600"
                                  data-testid={`edit-payroll-${payroll.id}`}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDownloadSlip(payroll.id, payroll.employee_name, payroll.month, payroll.year)}
                                title="Download Payslip"
                                className="text-blue-600"
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                              {payroll.status !== 'paid' && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDeletePayroll(payroll)}
                                  title="Delete Payroll"
                                  className="text-red-600"
                                  data-testid={`delete-payroll-${payroll.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Salary Structure Modal */}
      {showSalaryModal && selectedEmployee && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto py-4">
          <Card className="w-full max-w-2xl mx-4 my-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>
                  {selectedEmployee.has_salary_structure ? 'Edit' : 'Setup'} Employee Payroll
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowSalaryModal(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <CardDescription>Configure payroll details for {selectedEmployee.employee_name}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 max-h-[70vh] overflow-y-auto">
              
              {/* Employee Details Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-blue-700 border-b pb-2">
                  <UserCircle className="h-4 w-4" />
                  EMPLOYEE DETAILS (For Payslip)
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Name on Payslip *</Label>
                    <Input
                      value={salaryForm.employee_name_on_slip}
                      onChange={(e) => setSalaryForm({...salaryForm, employee_name_on_slip: e.target.value})}
                      placeholder="Full name as it appears on payslip"
                    />
                  </div>
                  <div>
                    <Label>Employee Code</Label>
                    <Input
                      value={salaryForm.employee_code}
                      onChange={(e) => setSalaryForm({...salaryForm, employee_code: e.target.value})}
                      placeholder="Auto-generated if empty"
                    />
                  </div>
                  <div>
                    <Label>Designation</Label>
                    <Input
                      value={salaryForm.designation}
                      onChange={(e) => setSalaryForm({...salaryForm, designation: e.target.value})}
                      placeholder="e.g. Sales Executive"
                    />
                  </div>
                  <div>
                    <Label>Department</Label>
                    <Input
                      value={salaryForm.department}
                      onChange={(e) => setSalaryForm({...salaryForm, department: e.target.value})}
                      placeholder="e.g. Sales, Marketing"
                    />
                  </div>
                  <div>
                    <Label>Date of Joining</Label>
                    <Input
                      type="date"
                      value={salaryForm.date_of_joining}
                      onChange={(e) => setSalaryForm({...salaryForm, date_of_joining: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Work Location</Label>
                    <Input
                      value={salaryForm.location}
                      onChange={(e) => setSalaryForm({...salaryForm, location: e.target.value})}
                      placeholder="e.g. Ahmedabad"
                    />
                  </div>
                </div>
              </div>

              {/* Salary Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-green-700 border-b pb-2">
                  <IndianRupee className="h-4 w-4" />
                  SALARY DETAILS
                </div>
                <div>
                  <Label>Monthly CTC *</Label>
                  <div className="relative mt-1">
                    <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="number"
                      value={salaryForm.monthly_ctc}
                      onChange={(e) => setSalaryForm({...salaryForm, monthly_ctc: e.target.value})}
                      placeholder="30000"
                      className="pl-9"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Auto-calculated: Basic (50%) | HRA (25%) | Conveyance (25%)
                  </p>
                </div>
              </div>
              
              {/* Bank Details Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-purple-700 border-b pb-2">
                  <CreditCard className="h-4 w-4" />
                  BANK & TAX DETAILS
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Bank Name</Label>
                    <Input
                      value={salaryForm.bank_name}
                      onChange={(e) => setSalaryForm({...salaryForm, bank_name: e.target.value})}
                      placeholder="HDFC Bank"
                    />
                  </div>
                  <div>
                    <Label>Account Number</Label>
                    <Input
                      value={salaryForm.bank_account_no}
                      onChange={(e) => setSalaryForm({...salaryForm, bank_account_no: e.target.value})}
                      placeholder="1234567890"
                    />
                  </div>
                  <div>
                    <Label>IFSC Code</Label>
                    <Input
                      value={salaryForm.ifsc_code}
                      onChange={(e) => setSalaryForm({...salaryForm, ifsc_code: e.target.value})}
                      placeholder="HDFC0001234"
                    />
                  </div>
                  <div>
                    <Label>PAN Number</Label>
                    <Input
                      value={salaryForm.pan_number}
                      onChange={(e) => setSalaryForm({...salaryForm, pan_number: e.target.value})}
                      placeholder="ABCDE1234F"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>UAN (Universal Account Number for PF)</Label>
                    <Input
                      value={salaryForm.uan_number}
                      onChange={(e) => setSalaryForm({...salaryForm, uan_number: e.target.value})}
                      placeholder="100XXXXXXXXX"
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowSalaryModal(false)}>Cancel</Button>
                <Button onClick={handleSaveSalary} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                  {loading ? 'Saving...' : 'Save Employee Setup'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Generate Payroll Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg mx-4">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Generate Payroll</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowGenerateModal(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <CardDescription>{monthNames[selectedMonth - 1]} {selectedYear}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Select Employee *</Label>
                <Select 
                  value={generateForm.user_id} 
                  onValueChange={(v) => setGenerateForm({...generateForm, user_id: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Choose employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees
                      .filter(e => e.has_salary_structure)
                      .filter(e => !payrolls.find(p => p.user_id === e.user_id))
                      .map(e => (
                        <SelectItem key={e.user_id} value={e.user_id}>
                          {e.employee_name} ({formatCurrency(e.monthly_ctc)})
                        </SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
                {employees.filter(e => e.has_salary_structure).filter(e => !payrolls.find(p => p.user_id === e.user_id)).length === 0 && (
                  <p className="text-xs text-orange-600 mt-1">
                    All employees with salary structure already have payroll generated for this month
                  </p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Total Working Days *</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={generateForm.total_days}
                    onChange={(e) => setGenerateForm({...generateForm, total_days: e.target.value})}
                    placeholder="26"
                  />
                </div>
                <div>
                  <Label>Paid Days * (supports half days like 15.5)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={generateForm.paid_days}
                    onChange={(e) => setGenerateForm({...generateForm, paid_days: e.target.value})}
                    placeholder="26 or 15.5"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Incentive</Label>
                  <div className="relative mt-1">
                    <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="number"
                      value={generateForm.incentive}
                      onChange={(e) => setGenerateForm({...generateForm, incentive: e.target.value})}
                      placeholder="0"
                      className="pl-9"
                    />
                  </div>
                </div>
                <div>
                  <Label>Loan/Advance Deduction</Label>
                  <div className="relative mt-1">
                    <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="number"
                      value={generateForm.loan_deduction}
                      onChange={(e) => setGenerateForm({...generateForm, loan_deduction: e.target.value})}
                      placeholder="0"
                      className="pl-9"
                    />
                  </div>
                </div>
              </div>
              
              <AdditionalEarningsEditor
                items={generateForm.additional_earnings}
                onChange={(items) => setGenerateForm({ ...generateForm, additional_earnings: items })}
              />
              
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowGenerateModal(false)}>Cancel</Button>
                <Button onClick={handleGeneratePayroll} disabled={loading}>
                  {loading ? 'Generating...' : 'Generate Payroll'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* View Payroll Modal */}
      {showViewModal && viewPayroll && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg mx-4">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Payroll Details</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowViewModal(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <CardDescription>
                {viewPayroll.employee_name} - {monthNames[viewPayroll.month - 1]} {viewPayroll.year}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Employee Code</p>
                  <p className="font-medium">{viewPayroll.employee_code}</p>
                </div>
                <div>
                  <p className="text-gray-500">Designation</p>
                  <p className="font-medium">{viewPayroll.designation}</p>
                </div>
                <div>
                  <p className="text-gray-500">Paid Days</p>
                  <p className="font-medium">{viewPayroll.paid_days} / {viewPayroll.total_days}</p>
                </div>
                <div>
                  <p className="text-gray-500">LOP Days</p>
                  <p className="font-medium">{viewPayroll.lop_days}</p>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <p className="font-medium mb-3 text-green-700">Earnings</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Basic</span>
                    <span>{formatCurrency(viewPayroll.basic)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>HRA</span>
                    <span>{formatCurrency(viewPayroll.hra)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Conveyance</span>
                    <span>{formatCurrency(viewPayroll.conveyance)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Incentive</span>
                    <span className="text-green-600">{formatCurrency(viewPayroll.incentive)}</span>
                  </div>
                  {(viewPayroll.additional_earnings || []).map((e, i) => (
                    <div className="flex justify-between" key={i} data-testid={`view-earning-${i}`}>
                      <span>{e.label}</span>
                      <span className="text-green-600">{formatCurrency(e.amount)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between font-medium border-t pt-2">
                    <span>Gross Earnings</span>
                    <span>{formatCurrency(viewPayroll.gross_earnings)}</span>
                  </div>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <p className="font-medium mb-3 text-red-700">Deductions</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Professional Tax</span>
                    <span>{formatCurrency(viewPayroll.professional_tax)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Loan/Advance</span>
                    <span>{formatCurrency(viewPayroll.loan_deduction)}</span>
                  </div>
                  <div className="flex justify-between font-medium border-t pt-2">
                    <span>Total Deductions</span>
                    <span className="text-red-600">{formatCurrency(viewPayroll.total_deductions)}</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-indigo-50 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-lg">Net Payable</span>
                  <span className="font-bold text-2xl text-indigo-700">{formatCurrency(viewPayroll.net_payable)}</span>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-2">
                {viewPayroll.status !== 'paid' && (
                  <Button 
                    variant="outline"
                    onClick={() => { setShowViewModal(false); openEditPayroll(viewPayroll); }}
                    className="gap-2 text-orange-600 border-orange-300 hover:bg-orange-50"
                  >
                    <Edit className="h-4 w-4" />
                    Edit Payroll
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  onClick={() => handleDownloadSlip(viewPayroll.id, viewPayroll.employee_name, viewPayroll.month, viewPayroll.year)}
                  className="gap-2"
                >
                  <Download className="h-4 w-4" />
                  Download Payslip
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Payroll Modal */}
      {showEditModal && editPayroll && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg mx-4">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Edit className="h-5 w-5 text-orange-600" />
                  Edit Payroll
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowEditModal(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <CardDescription>
                {editPayroll.employee_name} - {monthNames[editPayroll.month - 1]} {editPayroll.year}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Employee Info */}
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="text-gray-500">Employee:</span>
                    <span className="ml-2 font-medium">{editPayroll.employee_name}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Code:</span>
                    <span className="ml-2 font-medium">{editPayroll.employee_code}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Total Days:</span>
                    <span className="ml-2 font-medium">{editPayroll.total_days}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Current LOP:</span>
                    <span className="ml-2 font-medium text-red-600">{editPayroll.lop_days} days</span>
                  </div>
                </div>
              </div>

              {/* Editable Fields */}
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">
                    Paid Days <span className="text-gray-400">(max: {editPayroll.total_days})</span>
                  </Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    max={editPayroll.total_days}
                    value={editForm.paid_days}
                    onChange={(e) => setEditForm({...editForm, paid_days: e.target.value})}
                    className="mt-1"
                    data-testid="edit-paid-days"
                  />
                  {editForm.paid_days && parseFloat(editForm.paid_days) !== editPayroll.paid_days && (
                    <p className="text-xs text-orange-600 mt-1">
                      LOP will change to: {editPayroll.total_days - parseFloat(editForm.paid_days)} days
                    </p>
                  )}
                </div>

                <div>
                  <Label className="text-sm font-medium">Performance Incentive (Rs.)</Label>
                  <Input
                    type="number"
                    step="100"
                    min="0"
                    value={editForm.incentive}
                    onChange={(e) => setEditForm({...editForm, incentive: e.target.value})}
                    className="mt-1"
                    placeholder="0"
                    data-testid="edit-incentive"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium">Loan/Advance Deduction (Rs.)</Label>
                  <Input
                    type="number"
                    step="100"
                    min="0"
                    value={editForm.loan_deduction}
                    onChange={(e) => setEditForm({...editForm, loan_deduction: e.target.value})}
                    className="mt-1"
                    placeholder="0"
                    data-testid="edit-loan"
                  />
                </div>
              </div>

              <AdditionalEarningsEditor
                items={editForm.additional_earnings}
                onChange={(items) => setEditForm({ ...editForm, additional_earnings: items })}
              />

              {/* Info Note */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                <p className="font-medium">Note:</p>
                <p>All salary components (Basic, HRA, Conveyance, PT) will be automatically recalculated based on the updated paid days. The new payslip will reflect these changes.</p>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => setShowEditModal(false)}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleUpdatePayroll}
                  disabled={loading}
                  className="bg-orange-600 hover:bg-orange-700 gap-2"
                  data-testid="save-payroll-btn"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PayrollPage;
